import Link from 'next/link';
import {
  AvatarGroup,
  Avatar as CAvatar,
  Flex,
  Stack,
  Skeleton,
  SkeletonCircle,
} from '@chakra-ui/react';

import { getProfileFullNameOrUsername } from 'src/app/profile/utils';
import { Avatar } from 'src/components/avatar';

import { InfoBlock } from 'src/components/info-block';

import { useContext } from 'react';
import { UserContext, UserContextType } from 'src/contexts/UserContext';
import type { TeamMember } from '../service';

const DISPLAY_MAXIMUM_MEMBERS = 3;

export function Team({
  rid,
  members,
  loading,
}: {
  rid: string;
  members: TeamMember[];
  loading: boolean;
}) {
  const { user } = useContext(UserContext) as UserContextType;
  const [starter, ...remainMembers] = members || [];

  const { editors, officialCreators } = remainMembers.reduce(
    (acc, member) => {
      if (Number(member.role) === 103) {
        return {
          ...acc,
          editors: [...acc.editors, member],
        };
      }
      if (Number(member.role) === 104) {
        return {
          ...acc,
          officialCreators: [...acc.officialCreators, member],
        };
      }
      return acc;
    },
    { editors: [], officialCreators: [] }
  );
  const topThreeEditors = editors.slice(0, DISPLAY_MAXIMUM_MEMBERS);
  const topThreeOfficialCreators = officialCreators.slice(
    0,
    DISPLAY_MAXIMUM_MEMBERS
  );
  const remainEditors = [
    ...topThreeEditors.slice(DISPLAY_MAXIMUM_MEMBERS),
    ...topThreeOfficialCreators.slice(DISPLAY_MAXIMUM_MEMBERS),
  ];

  const isOwner = starter?.username === user?.username;

  return (
    <InfoBlock
      alignItems="flex-start"
      direction="column"
      spacing={3}
      p={5}
      position="relative"
      width={{ base: '100%', xl: '300px' }}
    >
      <header className="font-bold text-black">Team</header>
      <div className="text-xs text-gray-400">Starter</div>

      <Link href={`/reppl/${rid}/team/${starter?.username}`} passHref>
        <a className="flex w-full">
          <Avatar
            src={starter?.profileImgURL}
            name={getProfileFullNameOrUsername(starter)}
            width={40}
            height={40}
            isLoading={loading}
            author={starter}
          />
          <Skeleton
            ml={3}
            isLoaded={!loading}
            width={loading ? '100px' : 'auto'}
            height="20px"
          >
            <div className="text-sm font-semibold">
              {getProfileFullNameOrUsername(starter)}
            </div>
          </Skeleton>
        </a>
      </Link>

      {topThreeEditors.length > 0 && (
        <div className="text-xs text-gray-400">Editors</div>
      )}

      {loading ? (
        [1, 2, 3].map((i) => (
          <Flex key={i} alignItems="center">
            <SkeletonCircle size="40px" />
            <Skeleton
              ml={3}
              isLoaded={!loading}
              width={loading ? '100px' : 'auto'}
              height="20px"
            />
          </Flex>
        ))
      ) : (
        <Stack width="100%" spacing={4} direction="column">
          {topThreeEditors.map((m) => (
            <Link
              key={m.userID}
              href={`/reppl/${rid}/team/${m?.username}`}
              passHref
            >
              <a className="flex w-full">
                <Avatar
                  src={m?.profileImgURL}
                  name={getProfileFullNameOrUsername(m)}
                  width={40}
                  height={40}
                  author={m}
                />
                <div className="text-sm font-semibold ml-4 truncate">
                  {getProfileFullNameOrUsername(m)}
                </div>
              </a>
            </Link>
          ))}
          {topThreeOfficialCreators.length > 0 && (
            <div className="text-xs text-gray-400">Official Creators</div>
          )}

          {topThreeOfficialCreators.map((m) => (
            <Link
              key={m.userID}
              href={`/reppl/${rid}/team/${m?.username}`}
              passHref
            >
              <a className="flex w-full">
                <Avatar
                  src={m?.profileImgURL}
                  name={getProfileFullNameOrUsername(m)}
                  width={40}
                  height={40}
                  author={m}
                />

                <div className="text-sm font-semibold ml-4 truncate">
                  {getProfileFullNameOrUsername(m)}
                </div>
              </a>
            </Link>
          ))}

          {remainEditors.length === 1 ? (
            <Link
              href={`/reppl/${rid}/team/${remainEditors[0]?.username}`}
              passHref
            >
              <a className="flex w-full">
                <Avatar
                  src={remainEditors[0]?.profileImgURL}
                  name={getProfileFullNameOrUsername(remainEditors[0])}
                  width={40}
                  height={40}
                  author={remainEditors[0]}
                />
                <div className="text-sm font-semibold ml-4 truncate">
                  {getProfileFullNameOrUsername(remainEditors[0])}
                </div>
              </a>
            </Link>
          ) : (
            <Link href={`/reppl/${rid}/team`} passHref>
              <AvatarGroup className="remain-editors" as="a" size="sm" max={5}>
                {remainEditors.map((m) => (
                  <CAvatar
                    key={m.userID}
                    name={getProfileFullNameOrUsername(m)}
                    src={m.profileImgURL}
                  />
                ))}
              </AvatarGroup>
            </Link>
          )}

          <Link href={`/reppl/${rid}/team`} passHref>
            <a className="w-full rounded-md text-denim-500 font-bold text-sm border block text-center py-1">
              {isOwner ? 'Manage' : 'View'} the team
            </a>
          </Link>
        </Stack>
      )}

      <style jsx global>{`
        .remain-editors > .chakra-avatar {
          width: 3rem;
          height: 3rem;
        }
        .remain-editors > .chakra-avatar__excess {
          font-weight: bold;
          background: transparent;
          color: #0278e4;
        }
      `}</style>
    </InfoBlock>
  );
}
